//동아리 검색 입력창
import React, { useState, useEffect, useRef } from "react";
import { LuSearch } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import "./SearchBar.css";

function SearchBar({ activeTag, onTagChange }) {
  const tags = ["전체","중앙", "비중앙"];

  const [searchTerm, setSearchTerm] = useState("");
  const [clubResults, setClubResults] = useState([]);
  const [lostResults, setLostResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  // useState 대신 useRef를 사용해 타이머 ID를 저장 → 값이 바뀌어도 리렌더링이 발생하지 않음
  const debounceTimerRef = useRef(null);
  // 검색 컨테이너 DOM 요소를 참조 → 외부 클릭 감지에 사용
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // 디바운싱: 사용자가 타이핑할 때마다 API를 호출하면 불필요한 요청이 급증하므로
  // 마지막 입력 후 300ms 동안 추가 입력이 없을 때만 실제 검색 쿼리를 실행
  useEffect(() => {
    // searchTerm이 바뀔 때마다 이전 타이머를 취소해 중복 요청 방지
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    if (!searchTerm.trim()) {
      setClubResults([]);
      setLostResults([]);
      setShowDropdown(false);
      return;
    }

    debounceTimerRef.current = setTimeout(async () => {
      // Promise.all로 동아리·분실물 쿼리를 병렬 실행 → 순차 실행 대비 응답속도 단축
      // ilike: 대소문자 구분 없이 부분 일치 검색 (SQL의 LIKE와 동일)
      const [{ data: clubs }, { data: lost }] = await Promise.all([
        supabase
          .from("clubs")
          .select("id, name, image_url")
          .eq("approval_status", "승인")   // 관리자가 승인한 동아리만 노출
          .ilike("name", `%${searchTerm}%`)
          .limit(4),
        supabase
          .from("lost_items")
          .select("id, title, item_type")
          .ilike("title", `%${searchTerm}%`)
          .limit(4),
      ]);

      setClubResults(clubs || []);
      setLostResults(lost || []);
      setShowDropdown(true);
    }, 300);

    // useEffect cleanup: 컴포넌트가 언마운트되거나 searchTerm이 다시 바뀌면
    // 실행 대기 중인 타이머를 취소해 메모리 누수 방지
    return () => clearTimeout(debounceTimerRef.current);
  }, [searchTerm]);

  // 외부 클릭 감지: 검색창 바깥을 클릭하면 드롭다운을 닫음
  // mousedown을 사용하는 이유 - click보다 먼저 발생해 드롭다운 항목 클릭이 먼저 처리됨
  useEffect(() => {
    function handleClickOutside(e) {
      // containerRef.current가 클릭 대상을 포함하지 않으면 외부 클릭으로 판단
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    // cleanup: 컴포넌트 언마운트 시 이벤트 리스너 제거해 메모리 누수 방지
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleSelectClub(club) {
    setSearchTerm("");
    setShowDropdown(false);
    navigate(`/clubs/${club.id}`);
  }

  function handleSelectLost(item) {
    setSearchTerm("");
    setShowDropdown(false);
    navigate("/lost", { state: { openItemId: item.id } });
  }

  const hasResults = clubResults.length > 0 || lostResults.length > 0;

  return (
    <div className="search-bar-container" ref={containerRef}>
      <div className="search-input-wrapper">
        <LuSearch className="search-icon" />
        <input
          type="text"
          placeholder="동아리, 분실물을 검색해보세요"
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => hasResults && setShowDropdown(true)}
        />
      </div>

      {showDropdown && (
        <div className="search-dropdown">
          {!hasResults ? (
            <div className="search-dropdown-empty">검색 결과가 없어요</div>
          ) : (
            <>
              {clubResults.length > 0 && (
                <>
                  <div className="search-dropdown-section-label">동아리</div>
                  {clubResults.map((club) => (
                    <div key={club.id} className="search-dropdown-item" onClick={() => handleSelectClub(club)}>
                      <div className="search-dropdown-img-wrap">
                        {club.image_url ? (
                          <img src={club.image_url} alt={club.name} className="search-dropdown-img" />
                        ) : (
                          <div className="search-dropdown-placeholder">{club.name?.charAt(0)}</div>
                        )}
                      </div>
                      <span className="search-dropdown-name">{club.name}</span>
                    </div>
                  ))}
                </>
              )}
              {lostResults.length > 0 && (
                <>
                  <div className="search-dropdown-section-label">분실물</div>
                  {lostResults.map((item) => (
                    <div key={item.id} className="search-dropdown-item" onClick={() => handleSelectLost(item)}>
                      <div className="search-dropdown-img-wrap">
                        <div className="search-dropdown-placeholder">
                          {item.item_type === "분실" ? "🔍" : "📦"}
                        </div>
                      </div>
                      <span className="search-dropdown-name">{item.title}</span>
                      <span className="search-dropdown-tag">{item.item_type}</span>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}

      <div className="search-tags-wrapper">
        {tags.map((tag) => (
          <button
            key={tag}
            className={`search-tag-btn ${activeTag === tag ? "active" : ""}`}
            onClick={() => onTagChange(tag)}
          >
            {tag}
          </button>
        ))}
      </div>
    </div>
  );
}

export default SearchBar;
